/* product-constructor.js — simple & robust: always-selectable dropdowns with smart resolution */

class ProductConstructor extends HTMLElement {
  constructor() {
    super();
    this.customSelects = [];              // the visible constructor selects
    this.nativeSelect = null;             // Shopify's hidden variant <select>
    this.variants = [];                   // [{label, value, available, values:{paramLower:valueLower}}]
    this.parameters = [];                 // display names in UI order
    this._isSyncing = false;
  }

  connectedCallback() {
    // All parameter dropdowns inside this component (NOT the "HP" product switcher)
    this.customSelects = this.querySelectorAll('.product-constructor__select--custom');
    this.nativeSelect = document.querySelector('.select .select__select');
    if (!this.nativeSelect || this.customSelects.length === 0) return;

    // Capture UI parameter names in display order
    this.parameters = Array.from(this.customSelects).map(s => s.dataset.parameter.trim());

    this.customSelects.forEach(select => {
      Array.from(select.options).forEach(opt => {
        if (!opt.value.trim()) opt.remove();
      });
    });

    // Parse Shopify's native variant options
    this._indexVariants();

    // Initialize UI from currently selected native variant
    this._applyVariantToUI(this._currentNativeVariant());

    // Wire listeners — no disabling/hiding; always allow switching
    this.customSelects.forEach(select => {
      select.addEventListener('change', (e) => {
        if (this._isSyncing) return;
        this._isSyncing = true;

        const changedParam = e.target.dataset.parameter.toLowerCase();
        const wantedValue = e.target.value.toLowerCase().trim();

        const resolved = this._resolveVariant(changedParam, wantedValue);

        if (resolved) {
          this._applyVariantToUI(resolved);
          this._syncNativeSelectToVariant(resolved);
        } else {
          // As a very last resort, stick with the current native variant
          const current = this._currentNativeVariant();
          this._applyVariantToUI(current);
          this._syncNativeSelectToVariant(current);
          console.warn('No available variant could be resolved; reverted to current.');
        }

        this._isSyncing = false;
      });
    });
  }

  /* -------------------- indexing & parsing -------------------- */

  _indexVariants() {
    const parseLabel = (label) => {
      const values = {};
      label.split(' \\ ').forEach(piece => {
        const [k, ...rest] = piece.split(':');
        if (!k || rest.length === 0) return;
        const key = k.trim().toLowerCase();
        const val = rest.join(':').trim().toLowerCase();
        values[key] = val;
      });
      return values;
    };

    this.variants = Array.from(this.nativeSelect.options).map(opt => {
      const raw = opt.textContent.trim();
      const isUnavailable = raw.toLowerCase().endsWith(' - unavailable');
      const label = isUnavailable ? raw.slice(0, -' - unavailable'.length) : raw;
      return {
        label,
        value: opt.value,
        available: !isUnavailable,
        values: parseLabel(label)
      };
    });
  }

  _currentNativeVariant() {
    const opt = this.nativeSelect.options[this.nativeSelect.selectedIndex];
    if (!opt) return this.variants.find(v => v.available) || this.variants[0];
    const normalized = opt.textContent.trim().replace(/ - unavailable$/i, '').toLowerCase();
    return this.variants.find(v => v.label.toLowerCase() === normalized) || this.variants[0];
  }

  /* -------------------- resolution logic -------------------- */

  _currentSelectionsLower() {
    const out = {};
    this.customSelects.forEach(sel => {
      out[sel.dataset.parameter.toLowerCase()] = sel.value.toLowerCase().trim();
    });
    return out;
  }

  _variantsMatchingAll(selections) {
    return this.variants.filter(
      v => v.available && Object.entries(selections).every(([k, val]) => !val || v.values[k] === val)
    );
  }

  _resolveVariant(changedParamLower, wantedValueLower) {
    // 1) try strict match for ALL current selections (including the new pick)
    const preciseSelections = this._currentSelectionsLower();
    preciseSelections[changedParamLower] = wantedValueLower;

    let matches = this._variantsMatchingAll(preciseSelections);
    if (matches.length > 0) return matches[0];

    // 2) no exact match; lock the changed param and find the "closest" available variant
    //    closest = matches the most of the *other* current selections
    const others = this._currentSelectionsLower();
    others[changedParamLower] = wantedValueLower; // ensure this param is pinned

    const candidates = this.variants.filter(
      v => v.available && v.values[changedParamLower] === wantedValueLower
    );
    if (candidates.length === 0) return null;

    const score = (v) => {
      let s = 0;
      for (const [k, val] of Object.entries(others)) {
        if (k === changedParamLower) continue;
        if (val && v.values[k] === val) s += 1;
      }
      return s;
    };

    let best = candidates[0];
    let bestScore = score(best);
    for (let i = 1; i < candidates.length; i++) {
      const sc = score(candidates[i]);
      if (sc > bestScore) {
        best = candidates[i];
        bestScore = sc;
      }
    }
    return best;
  }

  /* -------------------- UI & native sync -------------------- */

  _applyVariantToUI(variant) {
    if (!variant) return;
    this.customSelects.forEach(select => {
      const pLower = select.dataset.parameter.toLowerCase();
      const vLower = variant.values[pLower];
      if (!vLower) return;
      const match = Array.from(select.options).find(o => o.value.toLowerCase() === vLower);
      if (match) select.value = match.value;
    });
  }

  _syncNativeSelectToVariant(variant) {
    if (!variant) return;
    this.nativeSelect.value = variant.value;
    this.nativeSelect.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

customElements.define('product-constructor', ProductConstructor);

/* ---------- keep your existing hidden line-item property syncing ---------- */
document.addEventListener('DOMContentLoaded', () => {
  const customSelects = document.querySelectorAll('.product-constructor__value--custom');
  const customProductProperties = document.querySelectorAll('.product-constructor__hidden-input');

  // Set initial values
  customSelects.forEach(select => {
    const parameter = select.dataset.parameter;
    let value;

    if (select.classList.contains('main-value')) {
      const selectedOption = select.options[select.selectedIndex];
      value = selectedOption.text.trim();
    } else {
      value = select.value;
      value = value.charAt(0).toUpperCase() + value.slice(1);
    }

    const matchingProperty = Array.from(customProductProperties).find(
      prop => prop.dataset.parameter === parameter
    );
    if (matchingProperty) matchingProperty.value = value;
  });

  // Update values on change
  customSelects.forEach(select => {
    select.addEventListener('change', (e) => {
      const parameter = e.target.dataset.parameter;
      let value;

      if (e.target.classList.contains('main-value')) {
        const selectedOption = e.target.options[e.target.selectedIndex];
        value = selectedOption.text.trim();
      } else {
        value = e.target.value;
      }

      const matchingProperty = Array.from(customProductProperties).find(
        prop => prop.dataset.parameter === parameter
      );
      if (matchingProperty) {
        const capitalizedValue = value.charAt(0).toUpperCase() + value.slice(1);
        matchingProperty.value = capitalizedValue;
      }
    });
  });
});